import { useState, useCallback, useRef, useEffect } from 'react';
import { 
    getAllUserFavorites, 
    addFavorite, 
    removeFavorite
} from '../services/api';

export function useFavorites(currentUserId = null) {
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pendingToggle, setPendingToggle] = useState(new Set());
    
    const loadedRef = useRef(false);

    // Load user's favorites
    const loadFavorites = useCallback(async () => {
        if (!currentUserId || loadedRef.current) return;
        
        try {
            setLoading(true);
            setError(null);
            
            const favoriteIds = await getAllUserFavorites(currentUserId);
            setFavorites(new Set(favoriteIds.map(String)));
            loadedRef.current = true;
        } catch (err) {
            console.error('Error loading favorites:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUserId]);

    // Toggle favorite status
    const toggleFavorite = useCallback(async (userId) => {
        if (!currentUserId || !userId) {
            return;
        }
        
        const userIdStr = String(userId);
        const isCurrentlyFavorite = favorites.has(userIdStr);
        
        // Optimistic update
        const newFavorites = new Set(favorites);
        if (isCurrentlyFavorite) {
            newFavorites.delete(userIdStr);
        } else {
            newFavorites.add(userIdStr);
        }
        setFavorites(newFavorites);
        
        // Track pending state
        const newPending = new Set(pendingToggle);
        newPending.add(userIdStr);
        setPendingToggle(newPending);
        
        try {
            if (isCurrentlyFavorite) {
                await removeFavorite(userId);
            } else {
                await addFavorite(userId);
            }
        } catch (err) {
            console.error('Error toggling favorite:', err);
            
            // Revert optimistic update on error
            const revertedFavorites = new Set(favorites);
            if (isCurrentlyFavorite) {
                revertedFavorites.add(userIdStr);
            } else {
                revertedFavorites.delete(userIdStr);
            }
            setFavorites(revertedFavorites);
            setError(err.message);
        } finally {
            // Remove from pending
            const updatedPending = new Set(pendingToggle);
            updatedPending.delete(userIdStr);
            setPendingToggle(updatedPending);
        }
    }, [currentUserId, favorites, pendingToggle]);

    // Check if a user is favorited
    const isFavorite = useCallback((userId) => {
        return favorites.has(String(userId));
    }, [favorites]);

    // Check if a toggle is pending
    const isPending = useCallback((userId) => {
        return pendingToggle.has(String(userId));
    }, [pendingToggle]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Auto-load favorites when currentUserId becomes available
    useEffect(() => {
        if (currentUserId && !loadedRef.current) {
            loadFavorites();
        }
    }, [currentUserId, loadFavorites]);

    return {
        favorites: Array.from(favorites),
        loading,
        error,
        loadFavorites,
        toggleFavorite,
        isFavorite,
        isPending,
        clearError
    };
}
